import { useCallback, useEffect, useState } from 'react';
import type { EntitlementMap, EntitlementValue, PlanId } from '@cyber-aware/shared-types';
import { useAuth } from '../auth/AuthContext';
import { jsonOrThrow } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export interface OrgEntitlements {
  planId: PlanId;
  planName: string;
  entitlements: EntitlementMap;
  overrides: EntitlementMap;
}

export async function getOrgEntitlements(
  idToken: string,
  orgId: string,
): Promise<OrgEntitlements> {
  const res = await fetch(`${API_URL}/v1/orgs/${orgId}/entitlements`, {
    headers: { authorization: `Bearer ${idToken}` },
  });
  return jsonOrThrow(res);
}

export function isEnabled(value: EntitlementValue | undefined): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return !!value;
}

// Module-level cache so multiple consumers (e.g. Sidebar nav + a page that
// gates on the same key) share a single network round-trip per session.
// Keyed by orgId and reset whenever the active org changes.
const cache = new Map<string, { data: OrgEntitlements; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;
const subscribers = new Set<() => void>();

function notify() {
  for (const fn of subscribers) fn();
}

/** Invalidate the cache for an org. Call after mutating the plan via the
 *  platform-admin endpoint so consumers in the same tab refresh. */
export function invalidateEntitlements(orgId: string) {
  cache.delete(orgId);
  notify();
}

/** React hook returning the effective entitlement set for the active org.
 *  Returns null while loading, then a stable object until the cache is
 *  invalidated or the active org changes. Use `useEntitlement(key)` for
 *  single-key gating — that's the usual ergonomic. */
export function useOrgEntitlements(): {
  data: OrgEntitlements | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const { session } = useAuth();
  const orgId = session?.activeOrgId ?? null;
  const [tick, setTick] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }, []);

  const cached = orgId ? cache.get(orgId) : undefined;
  const fresh = cached && Date.now() - cached.ts < CACHE_TTL_MS;

  useEffect(() => {
    if (!session || !orgId || fresh) return;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await session.user.getIdToken();
        const data = await getOrgEntitlements(idToken, orgId);
        if (cancelled) return;
        cache.set(orgId, { data, ts: Date.now() });
        notify();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // tick is here so cache invalidations trigger a re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, orgId, fresh, tick]);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    invalidateEntitlements(orgId);
  }, [orgId]);

  return {
    data: cached?.data ?? null,
    loading: !cached && !!orgId,
    error,
    refresh,
  };
}

/** Single-key gate. Returns `false` while loading so guarded UI hides until
 *  we know — preferred over flashing the feature on then off. */
export function useEntitlement(key: string): boolean {
  const { data } = useOrgEntitlements();
  return isEnabled(data?.entitlements[key]);
}

/** Numeric quota lookup (e.g. maxMembers). Returns null while loading. */
export function useEntitlementValue(key: string): EntitlementValue | null {
  const { data } = useOrgEntitlements();
  if (!data) return null;
  return data.entitlements[key] ?? null;
}
